/**
 * GlobalPdfDropOverlay — Overlay visual + modal de disambiguação
 * para drag global de PDF
 */

import { BookOpen, FolderOpen } from 'lucide-react';

interface GlobalPdfDropOverlayProps {
  dropState: 'idle' | 'dragging' | 'processing' | 'disambiguate';
  processingProgress?: { current: number; total: number };
  onChooseReference: () => void;
  onChooseResearchData: () => void;
  onDismiss: () => void;
}

export function GlobalPdfDropOverlay({
  dropState,
  processingProgress: _processingProgress,
  onChooseReference,
  onChooseResearchData,
  onDismiss,
}: GlobalPdfDropOverlayProps) {
  if (dropState === 'idle') return null;

  // Drag overlay
  if (dropState === 'dragging') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.55)',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            padding: '48px 64px',
            borderRadius: '16px',
            border: '3px dashed #477B72',
            background: 'rgba(71, 123, 114, 0.15)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>
            {'\uD83D\uDCC4'}
          </div>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#fff',
              letterSpacing: '-0.01em',
            }}
          >
            Solte para adicionar
          </div>
          <div
            style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginTop: '4px',
            }}
          >
            à Biblioteca
          </div>
        </div>
      </div>
    );
  }

  // Processing: handled by ImportProgressPanel — no overlay needed
  if (dropState === 'processing') return null;

  // Disambiguation modal
  if (dropState === 'disambiguate') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.5)',
        }}
        onClick={onDismiss}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '28px 32px',
            maxWidth: '380px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 20px',
            }}
          >
            O que é este arquivo?
          </h3>

          <button
            onClick={onChooseReference}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '14px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              marginBottom: '10px',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#477B72';
              e.currentTarget.style.background = 'rgba(71, 123, 114, 0.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.background = '#fff';
            }}
          >
            <BookOpen size={20} style={{ color: '#477B72', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                Referência bibliográfica
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                Artigo, livro, tese, capítulo...
              </div>
            </div>
          </button>

          <button
            onClick={onChooseResearchData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '14px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#477B72';
              e.currentTarget.style.background = 'rgba(71, 123, 114, 0.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.background = '#fff';
            }}
          >
            <FolderOpen size={20} style={{ color: '#8B6914', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                Dado de pesquisa
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                Entrevista, campo, experimento...
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return null;
}
